import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { FieldGroup, PillButton, Row, Select, Stack, Text, Title } from 'library/ui';
import { client, consoleLogInDev, isValidZipCode, isValidFormInputString } from 'shared';
import States from 'constants/States';

import { useUserContext } from 'UserContext';
import useContact from 'parent-portal/profile/BusinessProfile/hooks/useContact';

import TextInput from './TextInput';
import ProgramTypes from './ProgramTypes';
import { getProgramTypes, IProgramType } from 'api/ProgramTypeApi';
import LocationTypes from 'parent-portal/BusinessLocation/LocationTypes';

const stateOptions = Object.values(States).map((name) => ({ key: name, value: name }));

const DEFAULT_NEW_CONTACT = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    contactPreference: 'EMAIL',
};

const DEFAULT_LOCATION = {
    street: { value: '', error: true, displayHelper: false },
    city: { value: '', error: true, displayHelper: false },
    state: { value: '', error: true, displayHelper: false },
    zip: { value: '', error: true, displayHelper: false },
    name: { value: '', error: false, displayHelper: false },
    programId: { value: '', error: false, displayHelper: false },
};

type LocationDetail = keyof typeof DEFAULT_LOCATION;

export default function AddBusinessLocationForm({
    cancel,
    complete,
    optionalContact,
}: {
    cancel: () => void;
    complete?: () => void;
    optionalContact?: boolean;
}) {
    const { refreshAlreadyLoggedInUser } = useUserContext();

    const { isNewContactValid, populateCurrentContactInfo } = useContact(DEFAULT_NEW_CONTACT, optionalContact);

    const [locationDetails, setLocationDetails] = useState(DEFAULT_LOCATION);
    const [programTypeOptions, setProgramTypeOptions] = useState<IProgramType[]>([]);
    const [programTypes, setProgramTypes] = useState<string[]>([]);
    const [locationTypes, setLocationTypes] = useState<number[]>([]);
    const [errorMessage, setErrorMessage] = useState('');

    const programIdsRequiredStates = ['ohio', 'illinois'];

    useEffect(() => {
        if (!locationDetails.state.value) {
            if (programTypes.length) {
                setProgramTypes([]);
            }
        } else {
            getProgramTypes(locationDetails.state.value)
                .then((response) => {
                    setProgramTypeOptions(response);
                    if (!response.length) {
                        setProgramTypes([]);
                    }
                })
                .catch(consoleLogInDev);
        }
    }, [locationDetails.state.value]);

    function checkIsError(key: LocationDetail, value: string) {
        if (['street', 'city'].includes(key)) {
            return !isValidFormInputString(value);
        } else if (key === 'state') {
            return !isValidFormInputString(value);
        } else if (key === 'zip') {
            return !isValidZipCode(value);
        } else if (key === 'programId') {
            return programIdsRequiredStates.includes(locationDetails.state.value?.toLowerCase() ?? '')
                ? !(value.length > 5 && value.length < 21)
                : false;
        }

        return false;
    }

    function setValue(key: LocationDetail, value: string) {
        const additionalChecks =
            key === 'state' && programIdsRequiredStates.includes(value?.toLowerCase() ?? '')
                ? {
                      programId: {
                          value: locationDetails.programId.value,
                          error: checkIsError('programId', value),
                          displayHelper: false,
                      },
                  }
                : {
                      programId: {
                          value: locationDetails.programId.value,
                          error: checkIsError('programId', value),
                          displayHelper: false,
                      },
                  };

        setLocationDetails({
            ...locationDetails,
            ...additionalChecks,
            ...{ [key]: { value, error: checkIsError(key, value), displayHelper: false } },
        });
    }

    function setDisplay(key: LocationDetail) {
        const error = checkIsError(key, locationDetails[`${key}`].value);
        setLocationDetails({
            ...locationDetails,
            ...{ [key]: { ...locationDetails[`${key}`], error, displayHelper: error } },
        });
    }

    function checkBeforeSubmit() {
        const newLocationDetails = { ...locationDetails };
        Object.keys(locationDetails).forEach((key: string) => {
            const error = checkIsError(key as LocationDetail, locationDetails[`${key as LocationDetail}`].value);
            newLocationDetails[`${key as LocationDetail}`] = {
                ...newLocationDetails[`${key as LocationDetail}`],
                error,
                displayHelper: error,
            };
        });
        setLocationDetails(newLocationDetails);

        return Object.values(newLocationDetails).every((x) => !x.error) && isNewContactValid();
    }

    function onAddAddress() {
        if (!checkBeforeSubmit()) return;

        client('api/business-location/', {
            body: {
                street: locationDetails.street.value,
                city: locationDetails.city.value,
                state: locationDetails.state.value,
                zip_code: locationDetails.zip.value,
                location_name: locationDetails.name.value,
                program_id: locationDetails.programId.value,
                program_types: programTypeOptions.filter((x) => programTypes.includes(x.name)).map((x) => x.id),
                location_types: locationTypes,
            },
        })
            .then((res) => {
                if (res.status === 500) {
                    setErrorMessage(res.message);
                } else {
                    refreshAlreadyLoggedInUser(onCancel);
                }
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }

    function onCancel() {
        complete?.();
        setLocationDetails(DEFAULT_LOCATION);
        populateCurrentContactInfo(DEFAULT_NEW_CONTACT);
        cancel();
    }

    function onProgramTypesChanges(option: string) {
        if (programTypes.includes(option)) {
            setProgramTypes(programTypes.filter((x) => x !== option));
        } else {
            setProgramTypes([...programTypes, option]);
        }
    }

    function getProgramTypesSection() {
        if (!programTypeOptions.length) {
            return;
        }

        return (
            <Stack gap="sm">
                <Text>Do you currently participate in any of the following state programs?</Text>
                <ProgramTypes
                    options={programTypeOptions.map((x) => x.name)}
                    programTypes={programTypes}
                    onProgramTypesChanged={(x) => onProgramTypesChanges(x)}
                />
            </Stack>
        );
    }

    const firstFields: LocationDetail[] = ['name', 'street', 'city'];
    const secondFields: LocationDetail[] = ['zip', 'programId'];

    const validLocation = Object.values(locationDetails).every((x) => !x.error) && isNewContactValid();

    return (
        <Container>
            <Stack gap="md">
                <Title size="h1">Add a Center Location</Title>
                {firstFields.map((field: LocationDetail) => (
                    <TextInput
                        error={locationDetails[`${field}`].error}
                        helperText={
                            locationDetails[`${field}`].error && locationDetails[`${field}`].displayHelper
                                ? `Please enter a valid ${field}.`
                                : undefined
                        }
                        key={field}
                        label={`${field === 'name' ? 'Location Name' : `${field.capitalize()} *`}`}
                        onBlur={() => setDisplay(field)}
                        onChange={(value: string) => setValue(field, value)}
                        value={locationDetails[`${field}`].value}
                    />
                ))}
                <FieldGroup label="State" required>
                    <Select
                        value={locationDetails.state.value || ''}
                        onChange={(v) => setValue('state', v ?? '')}
                        options={stateOptions}
                        placeholder="Select a state"
                        fullWidth
                    />
                </FieldGroup>
                {secondFields.map((field: LocationDetail) => (
                    <TextInput
                        error={locationDetails[`${field}`].error}
                        helperText={
                            locationDetails[`${field}`].error && locationDetails[`${field}`].displayHelper
                                ? `Please enter a valid ${field}.`
                                : undefined
                        }
                        key={field}
                        label={field === 'zip' ? 'Zip *' : 'Program ID **'}
                        onBlur={() => setDisplay(field)}
                        onChange={(value: string) => setValue(field, value)}
                        value={locationDetails[`${field}`].value}
                    />
                ))}
                {getProgramTypesSection()}
                <LocationTypes selectedTypes={locationTypes} setSelectedTypes={setLocationTypes} />
                <Text size="sm" tone="secondary">
                    * Required
                </Text>
                <Text size="sm" tone="secondary">
                    ** Required in the state of Ohio and Illinois
                </Text>
                <Row justify="end" gap="sm">
                    <PillButton variant="secondary" onClick={onCancel}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={!validLocation} onClick={onAddAddress}>
                        Add
                    </PillButton>
                </Row>
                {errorMessage && <Text tone="error">{errorMessage}</Text>}
            </Stack>
        </Container>
    );
}

const Container = styled('div')({
    padding: 30,
});
